// components/DoctorMessageDialog.tsx
// 医師がユーザーへメッセージを送るダイアログ（送信履歴表示付き）
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Send, MessageSquare, Clock } from 'lucide-react';
import api from '../api/axios';

interface DoctorMessage {
  id: number;
  content: string;
  created_at: string;
  read_at: string | null;
}

interface Props {
  reservationId: number;
  patientName?: string;
  onClose: () => void;
}

function timeAgo(dateStr: string): string {
  const utcStr = dateStr.endsWith('Z') || dateStr.includes('+') ? dateStr : dateStr + 'Z';
  const diff = Date.now() - new Date(utcStr).getTime();
  const min  = Math.floor(diff / 60000);
  if (min < 1)  return 'たった今';
  if (min < 60) return `${min}分前`;
  const h = Math.floor(min / 60);
  if (h < 24)   return `${h}時間前`;
  const d = Math.floor(h / 24);
  if (d < 30)   return `${d}日前`;
  return new Date(dateStr).toLocaleDateString('ja-JP');
}

const DoctorMessageDialog: React.FC<Props> = ({ reservationId, patientName, onClose }) => {
  const { t } = useTranslation();
  const [messages, setMessages] = useState<DoctorMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const listRef = useRef<HTMLDivElement>(null);

  // メッセージ履歴を取得
  const loadMessages = async () => {
    try {
      const res = await api.get<DoctorMessage[]>(`/doctor-messages/${reservationId}`);
      setMessages(res.data);
    } catch {
      // エラーは無視（履歴なしとして扱う）
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMessages();
  }, [reservationId]);

  // 新規メッセージ送信後にリストを下へスクロール
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = async () => {
    if (!content.trim()) return;
    setSending(true);
    setError('');
    try {
      await api.post(`/doctor-messages/${reservationId}`, { content: content.trim() });
      setContent('');
      await loadMessages();
    } catch {
      setError('送信に失敗しました。もう一度お試しください。');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSend();
    }
  };

  const patientLabel = patientName || '患者';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col" style={{ height: '520px' }}>

        {/* ヘッダー */}
        <div className="shrink-0 flex items-center justify-between px-5 py-3.5 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center">
              <MessageSquare size={15} className="text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-800">{patientLabel}へのメッセージ</p>
              <p className="text-[10px] text-gray-400">{messages.length}件の送信履歴</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition">
            <X size={16} />
          </button>
        </div>

        {/* メッセージ履歴 */}
        <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-gray-50/50">
          {loading ? (
            <div className="flex items-center justify-center h-full text-gray-400 text-sm">
              読み込み中...
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-2 text-gray-400">
              <MessageSquare size={28} className="opacity-40" />
              <p className="text-sm">まだメッセージはありません</p>
            </div>
          ) : (
            messages.map((msg) => (
              <div key={msg.id} className="flex flex-col items-end gap-1">
                {/* 吹き出し（医師側 = 右） */}
                <div className="max-w-[85%] bg-blue-600 text-white rounded-2xl rounded-br-sm px-4 py-2.5 shadow-sm">
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                </div>
                {/* メタ情報 */}
                <div className="flex items-center gap-2 text-[10px] text-gray-400 pr-1">
                  <Clock size={9} />
                  <span>{timeAgo(msg.created_at)}</span>
                  {msg.read_at ? (
                    <span className="text-emerald-500">既読</span>
                  ) : (
                    <span className="text-gray-300">未読</span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* 入力エリア */}
        <div className="shrink-0 border-t border-gray-100 p-3">
          {error && <p className="text-red-500 text-xs mb-2 px-1">{error}</p>}
          <div className="flex gap-2 items-end">
            <textarea
              className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition"
              rows={3}
              placeholder={t('consultation.message_placeholder') || '患者へのメッセージを入力（Ctrl+Enterで送信）'}
              value={content}
              onChange={e => setContent(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={sending}
            />
            <button
              onClick={handleSend}
              disabled={sending || !content.trim()}
              className="shrink-0 flex items-center justify-center w-10 h-10 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition shadow-sm"
              title="送信 (Ctrl+Enter)"
            >
              {sending
                ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                : <Send size={16} />
              }
            </button>
          </div>
          <p className="text-[10px] text-gray-400 mt-1.5 px-1">Ctrl + Enter で送信</p>
        </div>

      </div>
    </div>
  );
};

export default DoctorMessageDialog;
